import { z } from 'zod';
import { emailSchema, uuidSchema, visuelSchema } from './common.js';
/**
 * Client — schéma partagé par le formulaire et par la validation serveur.
 * Écrit une fois : une règle dupliquée finit toujours par diverger.
 */
export const customerTypeSchema = z.enum(['individual', 'company']);
const optionalText = (max) => z
    .string()
    .trim()
    .max(max, `Au plus ${max} caractères`)
    .optional()
    // Un champ vidé par l'utilisateur doit devenir NULL, pas la chaîne vide :
    // sans cela, « email = '' » casse l'unicité et les recherches.
    .transform((v) => (v === '' ? undefined : v));
export const customerInputSchema = z
    .object({
    id: uuidSchema.optional(),
    type: customerTypeSchema.default('individual'),
    code: optionalText(40),
    firstName: optionalText(80),
    lastName: optionalText(80),
    companyName: optionalText(160),
    taxId: optionalText(60),
    /**
     * Date de naissance, au format `AAAA-MM-JJ`.
     *
     * ⭐ Texte et non `Date` : une date de naissance n'a pas d'heure. La porter
     * comme instant la rendrait dépendante du fuseau, et un client né le 3 mars
     * s'afficherait né le 2 mars pour qui consulte depuis un autre fuseau.
     *
     * ⚠️ Les deux bornes ne sont pas décoratives. Une date FUTURE est toujours
     * une faute de saisie ; au-delà de 120 ans, c'est presque toujours l'année
     * courante tapée à la place de l'année de naissance. Les refuser ici permet
     * de l'EXPLIQUER, ce qu'une contrainte SQL ne saurait pas faire.
     */
    birthDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .refine((v) => !Number.isNaN(Date.parse(v)), 'Date inexistante')
        .refine((v) => v <= new Date().toISOString().slice(0, 10), 'La date ne peut pas être future')
        .refine((v) => Number(v.slice(0, 4)) >= new Date().getFullYear() - 120, 'Année trop ancienne')
        .optional()
        .or(z.literal('').transform(() => undefined)),
    email: emailSchema.optional().or(z.literal('').transform(() => undefined)),
    phone: optionalText(30),
    mobile: optionalText(30),
    /**
     * L'INTERLOCUTEUR, quand le client est une société.
     *
     * ⭐ Une société ne décroche pas le téléphone : quelqu'un le fait pour
     * elle. Sans ces champs, le nom de la personne qu'on appelle finissait
     * dans les notes — introuvable à la recherche, absent des listes, et perdu
     * le jour où la note est réécrite.
     *
     * ⚠️ Jamais imposés, même à une société : une fiche se remplit souvent en
     * plusieurs fois, et refuser une raison sociale au motif qu'on ignore
     * encore le nom de l'acheteur ferait perdre le reste de la saisie. C'est
     * le formulaire qui ne les MONTRE qu'aux sociétés, et cela suffit — une
     * fiche de particulier n'a pas d'interlocuteur distinct d'elle-même.
     */
    contactFirstName: optionalText(80),
    contactLastName: optionalText(80),
    /** Fonction — « Directrice des achats ». Dit à qui l'on s'adresse. */
    contactRole: optionalText(80),
    contactPhone: optionalText(30),
    contactEmail: emailSchema.optional().or(z.literal('').transform(() => undefined)),
    addressLine1: optionalText(160),
    addressLine2: optionalText(160),
    postalCode: optionalText(20),
    city: optionalText(80),
    countryCode: z
        .string()
        .length(2, 'Code pays sur 2 lettres — exemple : CM')
        .toUpperCase()
        .optional(),
    currencyCode: z.string().length(3, 'Code devise sur 3 lettres — exemple : XAF').toUpperCase().optional(),
    paymentTermsDays: z.coerce
        .number()
        .int('Nombre de jours entier')
        .min(0, 'Ne peut pas être négatif')
        .max(365, 'Au plus 365 jours')
        .default(0),
    /**
     * Portrait en data URI, redimensionné côté navigateur.
     *
     * ⚠️ MEME schéma que les visuels d'articles, délibérément. Les deux
     * définitions vivaient séparément et ont divergé : le plafond des articles
     * a suivi le passage au format 640×854, celui des clients est resté en
     * arrière, et les portraits étaient refusés sans que rien ne le dise.
     */
    avatarUrl: visuelSchema.optional().or(z.literal('').transform(() => undefined)),
    notes: optionalText(4000),
    tags: z.array(z.string().trim().min(1).max(40)).max(20, 'Au plus 20 étiquettes').default([]),
})
    .refine((v) => v.type === 'company'
    ? Boolean(v.companyName)
    : Boolean(v.lastName) || Boolean(v.companyName), {
    // Message porté par le champ concerné, pour l'afficher au bon endroit.
    message: 'Renseignez au moins le nom (ou la raison sociale pour une société)',
    path: ['lastName'],
});
/** Filtres de la liste (W-08). Ils vivent dans l'URL, jamais dans un état local. */
export const customerQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    city: z.string().trim().max(80).optional(),
    type: customerTypeSchema.optional(),
    tag: z.string().trim().max(40).optional(),
    sort: z
        .enum(['displayName', '-displayName', 'lastActivityAt', '-lastActivityAt', 'createdAt', '-createdAt'])
        .default('displayName'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
//# sourceMappingURL=customer.js.map